import { useMemo, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import StarsIcon from '@mui/icons-material/Stars'
import { Box, Button, Grid, IconButton, Stack } from '@mui/material'
import { GridEventListener } from '@mui/x-data-grid'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useDebounce } from 'use-debounce'

import { ControlledTextField, FplDataGrid, IGridColumn } from 'components/common'
import { DEBOUNCE_DELAY } from 'constants/index'
import { DriverFilterInput } from 'generated/graphql'
import { GET_ALL_DRIVERS } from 'graphql/queries'

import CreateUpdateSubDrivers from '../AllDrivers/CreateUpdateSubDrivers/CreateUpdateSubDrivers'

interface IProps {
  driverId: number
}

const AllDrivers = (props: IProps) => {
  const { driverId } = props
  const [selectedSubDriverId, setSelectedSubDriverId] = useState<number | null>(null)
  const [selectedSubDriverRole, setSelectedSubDriverRole] = useState<string | null>(null)
  const [openSubDriverDialog, setOpenSubDriverDialog] = useState(false)

  const { control, watch, setValue } = useForm({
    shouldUnregister: true,
    defaultValues: {
      search: '',
    },
  })

  const columns: IGridColumn[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 200,
        flex: 1,
        renderCell: ({ row, value }) => (
          <Stack direction='row' alignItems='center' spacing={1}>
            {Number(row.id) === driverId && <StarsIcon fontSize='small' color='primary' />}
            <span>{value}</span>
          </Stack>
        ),
      },
      { field: 'role', headerName: 'Role', minWidth: 200, flex: 1 },
      { field: 'email', headerName: 'Email', minWidth: 200, flex: 1 },
      {
        field: 'isUsingInternalApp',
        headerName: 'Using internal app',
        sortable: false,
        maxWidth: 200,
        flex: 1,
        type: 'boolean',
      },
      { field: 'telephoneNumber', headerName: 'Telephone Number', minWidth: 200, flex: 1 },
      { field: 'isActive', headerName: 'Active', type: 'boolean', minWidth: 200, flex: 1 },
    ],
    [driverId],
  )

  const [searchInput] = useDebounce(watch('search', ''), DEBOUNCE_DELAY)
  const searchValue: string = searchInput.length >= 3 ? searchInput : ''

  const handleRowClick: GridEventListener<'rowClick'> = ({ row }) => {
    const [id, role] = [Number(row.id), String(row.role)]

    if (id === driverId) return

    setSelectedSubDriverId(id)
    setSelectedSubDriverRole(role)
    setOpenSubDriverDialog(true)

    if (!role) {
      toast.warning(`To update this sub-driver's role, please create an identity account for them.`)
    }
  }

  const handleCreateSubDriver = () => {
    setSelectedSubDriverId(null)
    setOpenSubDriverDialog(true)
  }

  const handleSearchClearClick = () => {
    setValue('search', '')
  }

  const handleCloseSubDriverDialog = () => {
    setOpenSubDriverDialog(false)
  }

  const filter: DriverFilterInput = useMemo(() => {
    return {
      or: [
        { name: { contains: searchValue } },
        { addressLine1: { contains: searchValue } },
        { telephoneNumber: { contains: searchValue } },
        { email: { contains: searchValue } },
      ],
    }
  }, [searchValue])

  return (
    <>
      <FplDataGrid
        query={GET_ALL_DRIVERS}
        queryVariables={{ driverId }}
        skipQuery={!driverId}
        entityName='allDrivers'
        columns={columns}
        filter={filter}
        defaultOrder={{ field: 'id', sort: 'asc' }}
        getRowClassName={(params) => (Number(params.row.id) === driverId ? 'owner-row' : '')}
        rootSx={{
          '& .owner-row': {
            cursor: 'not-allowed',
            pointerEvents: 'none',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.disabled,
            },
            color: (theme) => theme.palette.text.primary,
            backgroundColor: '#F5F5F5',
            borderLeft: '3px solid #9C27B0'
          },
        }}
        toolbar={{
          caption: 'Drivers',
          leftSide: (
            <Grid item>
              <Button variant='outlined' size='small' onClick={handleCreateSubDriver}>
                New Sub-driver
              </Button>
            </Grid>
          ),
          rightSide: (
            <Grid item>
              <Box width='210px'>
                <ControlledTextField
                  control={control}
                  name='search'
                  label='Search'
                  defaultValue=''
                  size='small'
                  endAdornment={
                    searchInput && (
                      <IconButton size='small' onClick={handleSearchClearClick}>
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    )
                  }
                />
              </Box>
            </Grid>
          ),
        }}
        onRowClick={handleRowClick}
      />
      {openSubDriverDialog && (
        <CreateUpdateSubDrivers
          subDriverId={selectedSubDriverId}
          subDriverRole={selectedSubDriverRole}
          driverId={driverId}
          openDriverDialog={openSubDriverDialog}
          onCloseDialog={handleCloseSubDriverDialog}
        />
      )}
    </>
  )
}

export default AllDrivers
